// Razorpay Integration Utility for AstroShubh
// Addresses mobile webview issues, UPI intent handling, and fallbacks for failed payments

// ─── SDK loaders ─────────────────────────────────────────────────────────────
// Razorpay ships two different scripts:
//   razorpay.js   — Custom Checkout SDK (exposes createPayment, no modal)
//   checkout.js   — Standard modal SDK  (exposes rzp.open, no createPayment)
// We cache each independently so they can coexist without conflicts.

const _sdkState = {
  custom:   { loaded: false, promise: null, src: 'https://checkout.razorpay.com/v1/razorpay.js' },
  standard: { loaded: false, promise: null, src: 'https://checkout.razorpay.com/v1/checkout.js' },
};

/**
 * Loads the Razorpay SDK appropriate for the given checkout mode.
 * @param {'custom'|'standard'} mode  Defaults to 'custom'.
 */
const loadRazorpaySDK = (mode = 'custom') => {
  const state = _sdkState[mode] || _sdkState.custom;

  if (state.promise) return state.promise;

  state.promise = new Promise((resolve) => {
    if (state.loaded && window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = state.src;
    script.async = true;

    script.onload = () => {
      state.loaded = true;
      console.log(`✅ Razorpay ${mode} SDK loaded`);
      resolve(true);
    };
    script.onerror = () => {
      console.error(`❌ Failed to load Razorpay ${mode} SDK`);
      state.promise = null; // allow retry
      resolve(false);
    };

    document.head.appendChild(script);
  });

  return state.promise;
};

/**
 * Enhanced Razorpay payment initialization with mobile webview support
 * @param {Object} options - Payment options
 * @param {string} options.razorpayKey - Razorpay key ID
 * @param {Object} options.order - Razorpay order object
 * @param {string} options.customerName - Customer name
 * @param {string} options.customerContact - Customer mobile number
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onFailure - Failure callback
 * @param {Object} options.notes - Additional notes for the order
 * @returns {Promise<void>}
 */
export const openRazorpayPayment = async ({
  razorpayKey,
  order,
  customerName,
  customerContact,
  onSuccess,
  onFailure,
  notes = {}
}) => {
  try {
    // openRazorpayPayment uses rzp.open() — needs the standard modal SDK (checkout.js)
    const sdkLoaded = await loadRazorpaySDK('standard');
    
    if (!sdkLoaded || !window.Razorpay) {
      throw new Error("Payment gateway unavailable. Please try opening in Chrome browser.");
    }

    // Enhanced options for better mobile compatibility
    const razorpayOptions = {
      key: razorpayKey,
      amount: order.amount,
      currency: order.currency || "INR",
      order_id: order.id,
      name: "AstroShubh",
      description: "Astrology Service Payment",
      image: "https://astroshubh.com/static/media/vedic-wisdom-logo.fe50f87d7676dc7eb80f.png", // Add your logo URL
      
      // Critical: Enable webview intent for better mobile app compatibility
      webview_intent: true,
      
      // Enhanced mobile payment handling
      config: {
        display: {
          blocks: {
            utib: {
              name: "Pay using UPI",
              instruments: [
                {
                  method: "upi"
                }
              ]
            },
            other: {
              name: "Other Payment Methods",
              instruments: [
                {
                  method: "card"
                },
                {
                  method: "netbanking"
                },
                {
                  method: "wallet"
                }
              ]
            }
          },
          hide: [
            {
              method: "paylater"
            }
          ],
          sequence: ["block.utib", "block.other"]
        }
      },
      
      // Customer prefill data
      prefill: {
        name: customerName,
        contact: customerContact,
        email: "" // Optional: add email if available
      },
      
      // Additional notes for tracking
      notes: {
        ...notes,
        platform: "web",
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      },
      
      // Success handler
      handler: function(response) {
        console.log("✅ Payment successful:", {
          payment_id: response.razorpay_payment_id,
          order_id: response.razorpay_order_id,
          signature: response.razorpay_signature
        });
        
        if (onSuccess) {
          onSuccess(response);
        }
      },
      
      // Modal configuration
      modal: {
        // Handle payment cancellation gracefully
        ondismiss: function() {
          console.log("⚠️ Payment modal dismissed by user");
          
          if (onFailure) {
            onFailure({
              error: {
                code: "PAYMENT_CANCELLED",
                description: "Payment was cancelled by user",
                source: "customer",
                step: "payment_modal",
                reason: "user_cancellation"
              }
            });
          }
        },
        
        // Prevent accidental dismissal on outside clicks
        escape: false,
        backdropclose: false
      },
      
      // Theme customization
      theme: {
        color: "#F6C600",
        backdrop_color: "rgba(0, 0, 0, 0.6)"
      },
      
      // Timeout configuration (30 minutes)
      timeout: 1800,
      
      // Enable automatic retry on network failures
      retry: {
        enabled: true,
        max_count: 3
      }
    };

    // Initialize Razorpay instance
    const razorpayInstance = new window.Razorpay(razorpayOptions);
    
    // Enhanced error handling for payment failures
    razorpayInstance.on("payment.failed", function(response) {
      console.error("❌ Payment failed:", {
        error: response.error,
        order_id: response.error?.metadata?.order_id,
        payment_id: response.error?.metadata?.payment_id
      });
      
      if (onFailure) {
        onFailure(response);
      }
    });
    
    // Handle network errors
    razorpayInstance.on("payment.error", function(response) {
      console.error("🌐 Payment network error:", response);
      
      if (onFailure) {
        onFailure({
          error: {
            code: "NETWORK_ERROR",
            description: "Network connectivity issue. Please check your connection and try again.",
            source: "network",
            step: "payment_processing",
            reason: "connectivity_issue"
          }
        });
      }
    });

    // Open payment modal
    razorpayInstance.open();
    
    console.log("🚀 Razorpay payment modal opened successfully");

  } catch (error) {
    console.error("💥 Error initializing Razorpay payment:", error);
    
    if (onFailure) {
      onFailure({
        error: {
          code: "INITIALIZATION_ERROR",
          description: error.message || "Failed to initialize payment gateway",
          source: "initialization",
          step: "sdk_loading",
          reason: "sdk_failure"
        }
      });
    }
  }
};

/**
 * Generates fallback payment instructions for failed payments
 * @param {number} amount - Payment amount in rupees
 * @param {string} orderId - Order ID for reference
 * @returns {Object} Fallback payment instructions
 */
export const generateFallbackPaymentInstructions = (amount, orderId) => {
  return {
    upiId: "bansal.shubham0017-1@oksbi", // Replace with your actual UPI ID
    qrCodeUrl: `upi://pay?pa=bansal.shubham0017-1@oksbi&pn=AstroShubh&am=${amount}&cu=INR&tn=Order%20${orderId}`,
    instructions: [
      "Some in-app browsers don't allow UPI apps to open properly.",
      "Please tap the three dots (⋮) → 'Open in Chrome' → pay again.",
      "Alternatively, you can:",
      "• Pay using our UPI ID: bansal.shubham0017-1@oksbi",
      "• Scan the QR code with any UPI app",
      "• Send payment screenshot on WhatsApp for confirmation"
    ],
    whatsappNumber: "919821660566", // Replace with your WhatsApp number (without +)
    supportMessage: "Sir/Ma'am, some in-app browsers don't allow UPI apps to open. Please tap the three dots → 'Open in Chrome' → pay again. Alternatively, you can pay using our UPI ID or QR code and send screenshot on WhatsApp."
  };
};

/**
 * Detects if the user is in an in-app browser that might cause payment issues
 * @returns {Object} Detection result with browser info and recommendations
 */
export const detectInAppBrowser = () => {
  const userAgent = navigator.userAgent || "";
  
  const inAppBrowsers = {
    instagram: /Instagram/i.test(userAgent),
    whatsapp: /WhatsApp/i.test(userAgent),
    facebook: /FBAN|FBAV|FB_IAB/i.test(userAgent),
    twitter: /Twitter/i.test(userAgent),
    linkedin: /LinkedInApp/i.test(userAgent),
    telegram: /TelegramBot/i.test(userAgent),
    snapchat: /Snapchat/i.test(userAgent),
    tiktok: /TikTok/i.test(userAgent)
  };

  const detectedBrowser = Object.keys(inAppBrowsers).find(browser => inAppBrowsers[browser]);

  return {
    isInAppBrowser: !!detectedBrowser,
    browserName: detectedBrowser || null,
    recommendation: detectedBrowser 
      ? `Detected ${detectedBrowser} browser. For better payment experience, please open this page in Chrome or default browser.`
      : null,
    userAgent
  };
};

/**
 * Shows browser compatibility warning if needed
 * @returns {boolean} True if warning was shown
 */
export const showBrowserCompatibilityWarning = () => {
  const detection = detectInAppBrowser();
  
  if (detection.isInAppBrowser && detection.browserName) {
    const message = `Payment may not work properly in ${detection.browserName} app browser. For best experience, please:\n\n1. Tap the three dots (⋮) or share button\n2. Select "Open in Chrome" or "Open in Browser"\n3. Try payment again\n\nAlternatively, you can use UPI ID or QR code payment method.`;
    
    alert(message);
    return true;
  }
  
  return false;
};

/**
 * Gets browser compatibility warning message without showing alert
 * @returns {string|null} Warning message or null if no warning needed
 */
export const getBrowserCompatibilityMessage = () => {
  const detection = detectInAppBrowser();
  
  if (detection.isInAppBrowser && detection.browserName) {
    return `Detected ${detection.browserName} browser. For better payment experience, please tap the three dots (⋮) → 'Open in Chrome' → try payment again.`;
  }
  
  return null;
};

/**
 * Validates UPI app availability on the device
 * Note: This is limited due to browser security, but can provide basic checks
 */
export const checkUPIAppAvailability = () => {
  // This is a basic check - actual UPI app detection is limited in browsers
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  return {
    isMobile,
    isAndroid,
    isIOS,
    recommendations: {
      android: ["PhonePe", "Google Pay", "Paytm", "Amazon Pay", "BHIM UPI"],
      ios: ["PhonePe", "Google Pay", "Paytm", "Amazon Pay", "BHIM UPI"]
    }
  };
};

// Export utility functions
export default {
  openRazorpayPayment,
  loadRazorpaySDK,
  generateFallbackPaymentInstructions,
  detectInAppBrowser,
  showBrowserCompatibilityWarning,
  getBrowserCompatibilityMessage,
  checkUPIAppAvailability
};